import * as THREE from 'three';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { WizardType } from '../wizards/WizardTypes';

const MODEL_PATHS: Record<WizardType | 'arena', string> = {
  pyromancer: '/models/pyromancer.glb',
  frostmage: '/models/frost_mage.glb',
  archmage: '/models/archmage.glb',
  battlemage: '/models/battlemage.glb',
  necromancer: '/models/necromancer.glb',
  druid: '/models/druid.glb',
  chronomancer: '/models/chronomancer.glb',
  arena: '/models/arena.glb'
};

class ModelLoader {
  private loader: GLTFLoader;
  private cache: Map<string, THREE.Group> = new Map();
  private loadingPromises: Map<string, Promise<THREE.Group>> = new Map();

  constructor() {
    this.loader = new GLTFLoader();
  }

  async loadModel(type: WizardType | 'arena'): Promise<THREE.Group> {
    const path = MODEL_PATHS[type];

    // Return cached model (cloned)
    if (this.cache.has(type)) {
      return this.cache.get(type)!.clone();
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(type)) {
      const cached = await this.loadingPromises.get(type)!;
      return cached.clone();
    }

    // Start loading
    const loadPromise = new Promise<THREE.Group>((resolve, reject) => {
      this.loader.load(
        path,
        (gltf: GLTF) => {
          const model = gltf.scene;

          // Enable shadows on all meshes
          model.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          // Cache the original
          this.cache.set(type, model);
          resolve(model.clone());
        },
        (progress) => {
          console.log(`Loading ${type}: ${(progress.loaded / progress.total * 100).toFixed(0)}%`);
        },
        (error) => {
          console.error(`Failed to load model ${type}:`, error);
          reject(error);
        }
      );
    });

    this.loadingPromises.set(type, loadPromise);
    return loadPromise;
  }

  async preloadAll(): Promise<void> {
    const types: (WizardType | 'arena')[] = ['pyromancer', 'frostmage', 'archmage', 'battlemage', 'arena'];
    await Promise.all(types.map(type => this.loadModel(type)));
    console.log('All models preloaded');
  }

  isLoaded(type: WizardType | 'arena'): boolean {
    return this.cache.has(type);
  }

  /** Check if a model is currently being loaded (but not yet cached) */
  isLoading(type: WizardType | 'arena'): boolean {
    return this.loadingPromises.has(type) && !this.cache.has(type);
  }
}

// Singleton instance
export const modelLoader = new ModelLoader();
